import type { FunctionComponent } from 'react';
import { Navigate, Outlet } from 'react-router';

import { LoadingOverlay } from '@mantine/core';

import { useAuth } from 'hooks/useAuth';

type ProtectedRouteWrapperProps = {
  allowCuboh?: boolean;
};

/**
 * Minimal protected route wrapper that handles authentication
 * but does NOT include AppShell/sidebar/navbar.
 * Use for full-screen pages like wizards that need auth but no chrome.
 *
 * By default, blocks Cuboh-only users (e.g. OTC campaigns).
 * Pass allowCuboh to permit Cuboh users (e.g. menu upload).
 */
export const ProtectedRouteWrapper: FunctionComponent<ProtectedRouteWrapperProps> = ({
  allowCuboh = false,
}) => {
  const { user, isInitialLoad, isLoading: isUserLoading, isUserCubohOnly } = useAuth();

  // Show loading while checking auth
  if (isInitialLoad || isUserLoading) {
    return <LoadingOverlay visible />;
  }

  // Redirect to login if not authenticated
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Cuboh-only users should not access routes under this wrapper
  // (e.g., OTC campaigns are not available to Cuboh users)
  if (!allowCuboh && isUserCubohOnly) {
    return <Navigate to="/" replace />;
  }

  return <Outlet />;
};
